import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import moment from 'moment';
import { Box, Text } from '@dutchie/common/components/rebass-compat';
import styled from 'styled-components';
import { CheckoutAccordion, CheckoutAccordionSummary, CheckoutAccordionActions } from 'shared/components';
import { DefaultInput, DateInput } from 'shared/components/text-input-with-label';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import { useUpdateUser } from 'src/state/actions/users';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import errors from 'shared/core/fieldValidationErrorMessages';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';

function Additional() {
  const [open, setOpen] = useState(false);
  const [driversLicense, setDriversLicense] = useState('');
  const [driversLicenseExpirationDate, setDriversLicenseExpirationDate] = useState('');
  const Checkout = useCheckout();
  const { Cart, User, UI } = useStores();
  const amplitude = useAmplitude();
  const updateUser = useUpdateUser();

  const isLoggedInUser = User.exists && !User.hasExternalUser;

  const isDutchiePlusKiosk = isDutchiePlusKioskOrder(Cart.orderType, UI.isPlus);

  // Opt-in dispensary setting (Checkout Settings > Identity Verification in admin). Defaults to
  // off, so existing dispensaries keep today's checkout behavior until they explicitly turn it on.
  const requiresExpirationDate = !!_.get(Cart, 'dispensary.requiresDriversLicenseExpirationDate', false);

  function resetFields() {
    const licenseOnOrder = _.get(Cart, 'order.driversLicense', '');
    const licenseExpirationOnOrder = _.get(Cart, 'order.driversLicenseExpirationDate', '');
    setDriversLicense(licenseOnOrder);
    setDriversLicenseExpirationDate(licenseExpirationOnOrder);
    if (_.isEmpty(licenseOnOrder) || (requiresExpirationDate && _.isEmpty(licenseExpirationOnOrder))) {
      setOpen(true);
    }
  }

  // reset fields on mount and open if empty.
  useEffect(() => {
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onCancel() {
    resetFields();
    setOpen(!open);
  }

  function onSave() {
    amplitude.log(AmplitudeEvents.checkout.clickSave, {
      componentName: `Drivers License`,
      dispensaryId: Cart?.dispensary?.id,
      dispensaryName: Cart?.dispensary?.name,
    });

    let hasError = false;
    if (driversLicense.length === 0) {
      Checkout.addErrors([{ path: 'driversLicense', message: errors.driversLicense.missing }]);
      hasError = true;
    }
    // NUG cannabis gap #1: expiration date is required alongside the license number, but only when
    // the dispensary has opted into requiring it (Checkout Settings > Identity Verification).
    // Validate format and expiration client-side (matching the backend validator) so the user gets
    // immediate feedback rather than a deferred error on order submission.
    if (requiresExpirationDate) {
      if (driversLicenseExpirationDate.length === 0) {
        Checkout.addErrors([
          { path: 'driversLicenseExpirationDate', message: errors.driversLicense.expirationMissing },
        ]);
        hasError = true;
      } else {
        const parsed = moment(driversLicenseExpirationDate, 'MM/DD/YYYY', true);
        if (!parsed.isValid()) {
          Checkout.addErrors([
            { path: 'driversLicenseExpirationDate', message: errors.driversLicense.expirationInvalid },
          ]);
          hasError = true;
        } else if (parsed.isBefore(moment().startOf('day'))) {
          Checkout.addErrors([{ path: 'driversLicenseExpirationDate', message: errors.driversLicense.expired }]);
          hasError = true;
        }
      }
    }

    if (!hasError) {
      Cart.setDriversLicenseNumber(driversLicense);
      Cart.setDriversLicenseExpirationDate(driversLicenseExpirationDate);

      if (isLoggedInUser) {
        User.profile.driversLicense = driversLicense;
        User.profile.driversLicenseExpirationDate = driversLicenseExpirationDate;

        updateUser(User);
      }

      setOpen(!open);
    }
  }

  const handleInputChange = (value) => {
    setDriversLicense(value);
    Cart.setDriversLicenseNumber(value);
  };

  const handleExpirationDateChange = (value) => {
    setDriversLicenseExpirationDate(value);
    Cart.setDriversLicenseExpirationDate(value);
  };

  return (
    <CheckoutLabelGrouping id='additional' data-testid='additional-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Additional Info</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        <CheckoutAccordion
          expanded={open}
          dataCyName='additional'
          onExpand={onCancel}
          onCancel={onCancel}
          onSave={onSave}
          error={
            Checkout.getError({ path: 'driversLicense' }).error ||
            Checkout.getError({ path: 'driversLicenseExpirationDate' }).error
          }
          defaultCancel={
            !!Cart.order.driversLicense && (!requiresExpirationDate || !!Cart.order.driversLicenseExpirationDate)
          }
          crossfade
        >
          <CheckoutAccordionSummary>
            <SummaryText fontSize={14} color='var(--dcm-checkout-text-color, #5d666d)' pt='2px'>
              {_.get(Cart, 'order.driversLicense', '').length > 0 && <b>Drivers License: </b>}
              {_.get(Cart, 'order.driversLicense', 'Please enter your drivers license number')}
            </SummaryText>
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <Box mt={!Cart.order.driversLicense ? '14px' : '24px'} mb='17px' width='100%'>
              <StyledDefaultInput
                id='driversLicenseNumber'
                data-cy='driver-license-field'
                data-test='driver-license-field'
                data-testid='driver-license-number-input'
                label='Drivers License Number'
                value={driversLicense}
                onChange={({ target }) => handleInputChange(target.value)}
                onValueAfterError={() => Checkout.removeError({ path: 'driversLicense' })}
                error={Checkout.getError({ path: 'driversLicense' }).error}
                errorMessage={Checkout.getError({ path: 'driversLicense' }).message}
                autoComplete={isDutchiePlusKiosk && 'off'}
                required
              />
              {requiresExpirationDate && (
                <StyledDateInput
                  mt='17px'
                  id='driversLicenseExpirationDate'
                  name='driversLicenseExpirationDate'
                  data-cy='driver-license-expiration-date-field'
                  data-test='driver-license-expiration-date-field'
                  data-testid='driver-license-expiration-date-input'
                  label='Drivers License Expiration Date'
                  value={driversLicenseExpirationDate}
                  onChange={({ target }) => handleExpirationDateChange(target.value)}
                  onValueAfterError={() => Checkout.removeError({ path: 'driversLicenseExpirationDate' })}
                  error={Checkout.getError({ path: 'driversLicenseExpirationDate' }).error}
                  errorMessage={Checkout.getError({ path: 'driversLicenseExpirationDate' }).message}
                  autoComplete={isDutchiePlusKiosk && 'off'}
                  required
                />
              )}
            </Box>
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(Additional);

const StyledDefaultInput = styled(DefaultInput)`
  width: 100%;
  border: 1px solid blue;
`;

const StyledDateInput = styled(DateInput)`
  width: 100%;
`;

const SummaryText = styled(Text)`
  span {
    font-weight: bold;
  }
`;
